import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RelatedItem, mapUsageSafetyFromDB } from "@/types/oil";
import { Product } from "@/types/product";

interface RelatedItemFromDB {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number | null;
}

function toRelatedItem(item: RelatedItemFromDB): RelatedItem {
  return {
    id: item.id,
    name: item.name,
    description: item.description || undefined,
    icon: item.icon || undefined,
    sortOrder: item.sort_order ?? undefined,
  };
}

function legacyToRelatedItems(items: string[]): RelatedItem[] {
  return items.map((name, index) => ({ id: `legacy-${index}`, name }));
}

const sortByOrder = (a: RelatedItem, b: RelatedItem) =>
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999);

function mapDBToProduct(db: any): Product {
  const categories = (db.hydrolat_category_relations?.map(
    (rel: any) => toRelatedItem(rel.hydrolat_categories)
  ) || []).sort(sortByOrder);

  const propertiesFromRelations = (db.hydrolat_property_relations?.map(
    (rel: any) => toRelatedItem(rel.hydrolat_properties)
  ) || []).sort(sortByOrder);

  const usesFromRelations = (db.hydrolat_use_relations?.map(
    (rel: any) => toRelatedItem(rel.hydrolat_uses)
  ) || []).sort(sortByOrder);

  const safetyNotesFromRelations = (db.hydrolat_safety_note_relations?.map(
    (rel: any) => toRelatedItem(rel.hydrolat_safety_notes)
  ) || []).sort(sortByOrder);

  return {
    id: db.hydrolat_id,
    uuid: db.id,
    name: db.name,
    nameEn: db.name_en,
    scientificName: db.scientific_name,
    description: db.description,
    safetyLevel: db.safety_level,
    properties: propertiesFromRelations.length > 0 ? propertiesFromRelations : legacyToRelatedItems(db.properties || []),
    uses: usesFromRelations.length > 0 ? usesFromRelations : legacyToRelatedItems(db.uses || []),
    safetyNotes: safetyNotesFromRelations.length > 0 ? safetyNotesFromRelations : legacyToRelatedItems(db.safety_notes || []),
    categories,
    imageUrl: db.image_url || undefined,
    productType: "hydrolat",
    ...mapUsageSafetyFromDB(db),
  };
}

async function fetchHydrolatsData() {
  const { data, error } = await supabase
    .from("hydrolats" as any)
    .select(`
      id, hydrolat_id, name, name_en, scientific_name, description,
      properties, uses, safety_level, safety_notes, image_url,
      can_aromatic, can_topical, can_internal,
      is_photosensitive, requires_dilution,
      safety_pregnant, safety_children, safety_babies, safety_pets,
      hydrolat_category_relations(
        hydrolat_categories(id, name, description, icon, sort_order)
      ),
      hydrolat_property_relations(
        hydrolat_properties(id, name, description, icon, sort_order)
      ),
      hydrolat_use_relations(
        hydrolat_uses(id, name, description, icon, sort_order)
      ),
      hydrolat_safety_note_relations(
        hydrolat_safety_notes(id, name, description, icon, sort_order)
      )
    `)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data as any[]) || [];
}

export function useHydrolats() {
  const { data: rawData = [], isLoading, error, refetch } = useQuery({
    queryKey: ["hydrolats"],
    queryFn: fetchHydrolatsData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const products = useMemo(() => rawData.map(mapDBToProduct), [rawData]);

  const categories = useMemo(() => {
    const all = new Set<string>();
    products.forEach(p => p.categories.forEach(c => all.add(c.name)));
    return ["Todos", ...Array.from(all).sort()];
  }, [products]);

  const searchProducts = useCallback((query: string): Product[] => {
    const lower = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      (p.nameEn?.toLowerCase().includes(lower)) ||
      (p.scientificName?.toLowerCase().includes(lower)) ||
      p.description.toLowerCase().includes(lower) ||
      p.properties.some(pr => pr.name.toLowerCase().includes(lower))
    );
  }, [products]);

  const getById = useCallback((id: string) => products.find(p => p.id === id), [products]);

  return { products, categories, loading: isLoading, error, searchProducts, getById, refetch: async () => { await refetch(); } };
}
